import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useReducer,
} from 'react';
import { Prescripteur } from '@europrocurement/l2d-domain';
import {
    AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
    RelationSocieteTiersJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { DataProviderProps, DataState } from './types';

export type TiersAddress = {
    id?: number;
    adresse1: string;
    adresse2: string;
    adresse3: string;
    adresse4: string;
    codePostal: string;
    ville: string;
    codePays?: string;
    fullAddress: string;
};

export type TiersRelationSociete = {
    societeId: number;
    codeClient?: string;
    clientSuivi?: boolean;
    clientXl?: boolean;
    tiersNonFiable?: boolean;
    dafc?: string;
    delaiPaiement?: number;
    devisDetaille?: boolean;
    modePaiement?: { id: number };
    portefeuille?: { id: number };
    categorieSocioProfessionnelle?: { id: number };
    emailSousclientObligatoire?: boolean;
    logoFormalite?: string;
    partenaireFormalite?: boolean;
};

export type TiersContact = {
    id: number;
    lastName: string;
    firstName: string;
    fonction: string;
    email?: string;
    phone?: string;
    mobile?: string;
    accounting: boolean;
    admin: boolean;
    main: boolean;
    userIdentifier?: string;
    active: boolean;
};

export type TiersDomainStatut = {
    id: number;
    libelle: string;
};

export type TiersDomaine = {
    id: number;
    libelle?: string;
    statut?: TiersDomainStatut;
    dateDebut?: string;
};

export type TiersCommercial = {
    id: number;
    utilisateur: {
        id?: number;
        nom?: string;
        prenom?: string;
        initiales?: string;
        email?: string;
        portable?: string;
        ligneDirecte?: string;
    };
    domaine?: {
        id?: number;
        libelle?: string;
    };
    actif?: boolean;
};

export type TiersMultibureau = {
    id: number;
};

export type Tiers = {
    id: number;
    raisonSociale: string;
    siren: string;
    tvaIntracommunautaire?: string;
    regimeTva?: string;
    codeComptable?: string;
    adresse: TiersAddress;
    contacts: TiersContact[];
    adresseFacturation: TiersAddress;
    prescripteurId?: number;
    demo?: boolean;
    relationsSocietes: TiersRelationSociete[];
    dateMAJ?: Date;
    dateCreation?: Date;
    domaines?: TiersDomaine[];
    commerciaux?: TiersCommercial[];
    multibureaux?: TiersMultibureau[];
};

type TiersState = DataState & {
    fetching: Array<{ tiersId: number; fetching: boolean }>;
    data: Array<Tiers>;
};

const formatAddress = (addr?: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup) => ({
    id: addr?.id,
    adresse1: addr?.adresse1 as string,
    adresse2: addr?.adresse2 as string,
    adresse3: addr?.adresse3 as string,
    adresse4: addr?.adresse4 as string,
    codePostal: addr?.codePostal as string,
    ville: addr?.ville as string,
    codePays: addr?.codePays,
    fullAddress: [
        addr?.adresse1,
        addr?.adresse2,
        addr?.adresse3,
        addr?.adresse4,
        addr?.codePostal,
        addr?.ville,
    ]
        .filter((s) => s)
        .join(', '),
});

export const tiersApiToStateTransformer = (item: Prescripteur, idSociete: number): Tiers => {
    const defaultAddress = item.adresses.find((addr) => addr.defaut) ?? item.adresses[0];
    const billingAddress = item.adresses.find((addr) => addr.facturation);

    return {
        id: item.id as number,
        raisonSociale: item.raisonSociale as string,
        siren: item.siren as string,
        tvaIntracommunautaire: item.tvaIntracommunautaire,
        regimeTva: item.regimeTva,
        codeComptable: item.relationsSocietes.find((relSoc) => relSoc.societe?.id === idSociete)
            ?.codeClient,
        prescripteurId: item.idPrescripteur,
        adresse: formatAddress(defaultAddress),
        adresseFacturation: formatAddress(billingAddress),
        demo: item.demo || false,
        dateMAJ: item.dateMAJ ? new Date(item.dateMAJ) : undefined,
        dateCreation: item.dateCreation ? new Date(item.dateCreation) : undefined,
        relationsSocietes: item.relationsSocietes
            .filter(
                (
                    relSoc: RelationSocieteTiersJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
                ) => relSoc.societe?.id !== undefined,
            )
            .map(
                (
                    relSoc: RelationSocieteTiersJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
                ) => ({
                    societeId: relSoc.societe!.id as number,
                    codeClient: relSoc.codeClient,
                    clientSuivi: relSoc.clientSuivi,
                    clientXl: relSoc.clientXl,
                    tiersNonFiable: relSoc.tiersNonFiable,
                    dafc: relSoc.dafc,
                    delaiPaiement: relSoc.delaiPaiement,
                    devisDetaille: relSoc.devisDetaille,
                    modePaiement:
                        relSoc.modePaiement?.id !== undefined
                            ? { id: relSoc.modePaiement.id }
                            : undefined,
                    portefeuille: relSoc.portefeuille ? { id: relSoc.portefeuille.id } : undefined,
                    categorieSocioProfessionnelle: relSoc.categorieSocioProfessionnelle
                        ? { id: relSoc.categorieSocioProfessionnelle.id }
                        : undefined,
                    emailSousclientObligatoire: relSoc.emailSousclientObligatoire,
                    logoFormalite: relSoc.logoFormalite,
                    partenaireFormalite: relSoc.partenaireFormalite,
                }),
            ),
        contacts: item.contacts.map((contact) => ({
            id: contact.id as number,
            lastName: contact.nomContact,
            firstName: contact.prenomContact,
            email: contact.emailsContact.find((mail) => mail.defaut)?.email,
            phone: contact.telephonesContact.find((ph) => ph.defaut)?.telephone,
            mobile: contact.mobilesContact.find((mo) => mo.defaut)?.mobile,
            fonction: contact.fonction as string,
            accounting: contact.comptabilite as boolean,
            admin: contact.administrateur as boolean,
            main: contact.principal as boolean,
            userIdentifier: contact.userIdentifier,
            active: !contact.isExit,
        })),
        domaines: Array.isArray(item.domaines)
            ? (item.domaines as Array<Record<string, unknown>>).map((d) => ({
                  id: d.id as number,
                  libelle: d.libelle as string | undefined,
                  statut: d.statut
                      ? {
                            id: (d.statut as Record<string, unknown>).id as number,
                            libelle: (d.statut as Record<string, unknown>).libelle as string,
                        }
                      : undefined,
                  dateDebut: d.createdAt as string | undefined,
              }))
            : undefined,
        multibureaux: Array.isArray(item.multibureaux)
            ? item.multibureaux
                  .filter((m) => m.id !== undefined && m.id !== null)
                  .map((m) => ({ id: m.id as number }))
            : [],
    };
};

export const ADD_TIERS_CTX = 'add_tiers_ctx';
export const ADD_MANY_TIERS_CTX = 'add_many_tiers_ctx';
export const START_FETCH_TIERS_CTX = 'start_fetch_tiers_ctx';
export const STOP_FETCH_TIERS_CTX = 'stop_fetch_tiers_ctx';
export const ERASE_FETCH_TIERS_CTX = 'erase_fetch_tiers_ctx';
export const UPDATE_TIERS_COMMERCIAUX_CTX = 'update_tiers_commerciaux_ctx';

type Action =
    | { type: 'init_tiers_ctx'; payload: Array<Tiers> }
    | { type: 'add_tiers_ctx'; payload: Tiers }
    | { type: 'add_many_tiers_ctx'; payload: Array<Tiers> }
    | { type: 'start_fetch_tiers_ctx'; payload: number }
    | { type: 'stop_fetch_tiers_ctx'; payload: number }
    | {
          type: 'update_tiers_commerciaux_ctx';
          payload: { tiersId: number; commerciaux: TiersCommercial[] };
      };

export const TiersContext = createContext<TiersState | null>(null);
export const TiersDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function tiersReducer(state: TiersState, action: Action) {
    switch (action.type) {
        case ADD_TIERS_CTX: {
            return {
                ...state,
                data: [...state.data.filter((t) => t.id !== action.payload.id), action.payload],
            };
        }
        case ADD_MANY_TIERS_CTX: {
            const incomingIds = new Set(action.payload.map((t) => t.id));
            return {
                ...state,
                data: [...state.data.filter((t) => !incomingIds.has(t.id)), ...action.payload],
            };
        }
        case START_FETCH_TIERS_CTX: {
            return {
                ...state,
                fetching: [
                    ...state.fetching.filter((f) => f.tiersId !== action.payload),
                    { tiersId: action.payload, fetching: true },
                ],
            };
        }
        case STOP_FETCH_TIERS_CTX: {
            return {
                ...state,
                fetching: [
                    ...state.fetching.filter((f) => f.tiersId !== action.payload),
                    { tiersId: action.payload, fetching: false },
                ],
            };
        }
        case UPDATE_TIERS_COMMERCIAUX_CTX: {
            return {
                ...state,
                data: state.data.map((t) =>
                    t.id === action.payload.tiersId
                        ? { ...t, commerciaux: action.payload.commerciaux }
                        : t,
                ),
            };
        }
        default: {
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

export const TiersProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ children }) {
        const [state, dispatch] = useReducer(tiersReducer, {
            loaded: true,
            fetching: [],
            data: [],
        });

        return (
            <TiersContext.Provider value={state}>
                <TiersDispatchContext.Provider value={dispatch}>
                    {children}
                </TiersDispatchContext.Provider>
            </TiersContext.Provider>
        );
    };

export function useTiersCtxDispatch() {
    return useContext(TiersDispatchContext);
}

export function useTiersCtx() {
    const state = useContext(TiersContext);

    const getTiers = useCallback((): Array<Tiers> => state?.data || [], [state?.data]);

    const getTiersById = useCallback(
        (id: number): Tiers | undefined => (state?.data || []).find((item) => item.id === id),
        [state?.data],
    );

    const getMultipleTiersFiltered = useCallback(
        (filters: Partial<Record<keyof Tiers, unknown>>): Array<Tiers> =>
            (state?.data || []).filter((item) =>
                Object.entries(filters).every(([key, value]) => item[key as keyof Tiers] === value),
            ),
        [state?.data],
    );

    const getTiersFiltered = useCallback(
        (filters: Partial<Record<keyof Tiers, unknown>>): Tiers | undefined =>
            (state?.data || []).find((item) =>
                Object.entries(filters).every(([key, value]) => item[key as keyof Tiers] === value),
            ),
        [state?.data],
    );

    const isFetch = useCallback(
        (tiersId: number): boolean | undefined => {
            const fetching = state?.fetching.find((f) => f.tiersId === tiersId);

            if (fetching) {
                return !fetching.fetching;
            }

            return false;
        },
        [state?.fetching],
    );

    return {
        getTiers,
        getTiersById,
        getMultipleTiersFiltered,
        getTiersFiltered,
        isFetch,
    };
}
